import { useState, useCallback } from 'react';
import { networkDetector } from '../services/networkDetector';
import { blockchainApi } from '../services/blockchainApi';
import type { Network, LookupState } from '../types/blockchain.types';

export function useAddressLookup() {
  const [state, setState] = useState<LookupState>({
    loading: false,
    error: null,
    addressInfo: null,
    selectedNetwork: null,
  });

  const lookupAddress = useCallback(async (address: string, networkOverride?: Network) => {
    if (!address.trim()) {
      setState({
        loading: false,
        error: 'Please enter an address',
        addressInfo: null,
        selectedNetwork: null,
      });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      let targetNetwork: Network | undefined;

      if (networkOverride) {
        targetNetwork = networkOverride;
      } else {
        const detection = networkDetector.detectAddress(address);

        if (detection.compatible_networks.length === 0) {
          setState({
            loading: false,
            error: 'Invalid address format. Please check the address and try again.',
            addressInfo: null,
            selectedNetwork: null,
          });
          return;
        }

        targetNetwork = detection.suggested_network || detection.compatible_networks[0];
      }

      const addressInfo = await blockchainApi.getAddressInfo(address, targetNetwork);

      setState({
        loading: false,
        error: null,
        addressInfo,
        selectedNetwork: targetNetwork,
      });
    } catch (error) {
      console.error('Error looking up address:', error);

      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to fetch address information. Please try again.';

      setState((prev) => ({
        loading: false,
        error: errorMessage,
        // Mantener addressInfo y selectedNetwork anteriores si existen
        addressInfo: prev.addressInfo,
        selectedNetwork: prev.selectedNetwork,
      }));
    }
  }, []);

  const setNetwork = useCallback((network: Network) => {
    setState((prev) => ({ ...prev, selectedNetwork: network }));
  }, []);

  const reset = useCallback(() => {
    setState({
      loading: false,
      error: null,
      addressInfo: null,
      selectedNetwork: null,
    });
  }, []);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    lookupAddress,
    setNetwork,
    reset,
    clearError,
  };
}
